import React, { ChangeEvent, useEffect, useState, useRef } from 'react'
import { noop } from 'lodash'
import { Icon } from '@pff-consumer/web-ui/components/icon/icon'
import { useFeatureFlags } from '@pff-consumer/feature-flags'
import { useAnalytics } from '@pff-consumer/analytics'
import { SVGGroup } from '@pff-consumer/utils'
import { StatusPane } from '@pff-consumer/tweed-ui/molecules/status-pane/status-pane.web'
import { CurrentProgressState } from '@pff-consumer/schema'
import { useCreateUserAccountMutation } from '@pff-consumer/data-provider'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/feature-account/hooks/use-subscribe-analytics-event.web'
import { useRouter } from 'next/router'
import ReCAPTCHA from 'react-google-recaptcha'
import * as Sentry from '@sentry/nextjs'
import { redirectBaseUrl } from '@pff-consumer/utils/lib/accounts/subscribe/constants'
import {
  setAccountSessionData,
  transformAccountsResponseToAccountSession,
} from '../../utils/session/create-account-session'
import { Button } from './button/button'
import { useStyles } from './create-account.styles'
import { createAccountformValidators, CreateAccountValidation, formValidators } from './create-account-validation'
import { CreateAccountSuccess } from '../create-account-success/create-account-success'
import { CreateAccountError } from '../create-account-error/create-account-error'
import { useRedirectTo } from '../../hooks/useRedirectTo'

// This is added because safari has a key icon, so in order to apply CSS to that, we need to define this
declare global {
  interface Window {
    // This only exists in safari browsers
    safari?: any
  }
}

// TODO: Fix so we let the HTML form control the state
export const CreateAccount = ({ isUSUser }: { isUSUser: boolean }) => {
  const [createAccountValidation, setCreateAccountValidation] = useState<CreateAccountValidation>(formValidators)
  const [email, setEmail] = useState<string>('')
  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true)
  const [isSafari, setSafari] = useState(false)
  const [password, setPassword] = useState<string>('')
  const [shouldSubscribeToNewsletter, setShouldSubscribeToNewsletter] = useState<boolean>(isUSUser)
  const [ignoreError, setIgnoreError] = useState<boolean>(false)
  const [createAccount, { error, isLoading: loading, isSuccess: success }] = useCreateUserAccountMutation()
  const { addAnalyticsUser } = useAnalytics()
  const { getFeatureFlag } = useFeatureFlags()
  const styles = useStyles({ isSafari })
  const router = useRouter()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { redirectTo } = useRedirectTo({ router })
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()

  useEffect(() => {
    setSafari(window && window?.safari !== undefined)
  }, [])

  const shouldDisplayGoogleSignIn = getFeatureFlag<boolean>('enable-google-social-sign-in', false)
  const shouldDisplayAppleSignIn = getFeatureFlag<boolean>('enable-apple-social-sign-in', false)
  const shouldDisplayFacebookSignIn = getFeatureFlag<boolean>('enable-facebook-sign-in', false)

  const processCreateAccount = async (recaptchaToken: string) => {
    const createAccountForm = createAccountformValidators(email, password)
    if (createAccountForm.validEmail || createAccountForm.validPassword) {
      setCreateAccountValidation(() => ({ ...createAccountForm }))
      sendSubscribeAnalyticsEvent('CTA Clicked', {
        ctaName: 'Create Account',
        ctaLocation: 'Create Free Account',
      })
      createAccount({
        email,
        password,
        shouldSubscribeToNewsletter,
        recaptchaToken,
      }).then((response) => {
        if (response?.error) {
          Sentry.captureException(new Error('Error with create account'))
        }

        if (response?.data) {
          const { data } = response

          if ('error' in data) {
            Sentry.captureException(new Error('Error with create account'))
          } else {
            const checkoutSessionData = transformAccountsResponseToAccountSession(data, email)

            setAccountSessionData(checkoutSessionData)

            // Will add the use to analytics
            addAnalyticsUser({ uid: data.uid, email })

            window.open(`/auto-login?redirect=${redirectTo}`, '_self')
          }
        }
      })
      setIgnoreError(false)
    }
  }

  const processCreateAccountWithRecaptcha = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (recaptchaRef.current) {
      const token = await recaptchaRef.current.executeAsync()
      if (token) {
        await processCreateAccount(token)
      }
    }

    return null
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    setEmail(e.target.value)
    const createAccountForm = createAccountformValidators(e.target.value, password)
    setCreateAccountValidation(() => ({ ...createAccountForm }))
  }

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const createAccountForm = createAccountformValidators(email, e.target.value)
    setCreateAccountValidation(() => ({ ...createAccountForm }))
    setPassword(e.target.value)
  }

  const handleSubscribedChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setShouldSubscribeToNewsletter(e.target.checked)
  }

  if (loading) {
    return (
      <div className={styles.createAccountContainer}>
        <StatusPane
          state={CurrentProgressState.IN_PROGRESS}
          header='Creating Your Account.'
          message='In the red zone going for it on 4th and 1.'
        />
      </div>
    )
  }

  if (error && !ignoreError) {
    // @ts-expect-error
    const errorMessage = error?.data?.error || 'unknown error'
    return (
      <CreateAccountError
        error={errorMessage}
        onClick={() => {
          setIgnoreError(true)
          setEmail('')
          setPassword('')
          setCreateAccountValidation({
            validEmail: true,
            emailErrorMessage: '',
            validPassword: false,
            isValid: false,
          })
          router.push({ query: { redirect_to: redirectTo } }, undefined, { shallow: true })
        }}
      />
    )
  }

  if (success) {
    return <CreateAccountSuccess />
  }

  return (
    <div className={styles.createAccountContainer}>
      <form
        onSubmit={processCreateAccountWithRecaptcha}
        className={styles.mainContainer}
      >
        <h1 className={styles.heading}>Create a FREE Account</h1>
        <h2 className={styles.headingText}>Create an account to sync your fantasy league for FREE today.</h2>
        <div>
          <div className={styles.textFieldContainer}>
            <label className={styles.textFieldLabel}>
              Email <span className={styles.mandatoryIndicator}> *</span>
            </label>
            <input
              type='email'
              placeholder='Email'
              name='email'
              className={createAccountValidation.validEmail ? styles.email : styles.invalidEmail}
              onBlur={handleEmailChange}
            />
            {!createAccountValidation.validEmail && (
              <div
                className={styles.errorMessage}
                data-testid='error-message'
              >
                <Icon
                  name='field-error'
                  group={SVGGroup.Icons}
                  width={12}
                  height={12}
                />
                {createAccountValidation.emailErrorMessage}
              </div>
            )}
          </div>
          <div className={styles.textFieldContainer}>
            <label className={styles.textFieldLabel}>
              Password <span className={styles.mandatoryIndicator}> *</span>
            </label>
            <input
              type={isPasswordHidden ? 'password' : 'text'}
              placeholder='Password'
              name='password'
              className={styles.password}
              onChange={handlePasswordChange}
            />
            <button
              className={styles.passwordIconContainer}
              type='button'
              onClick={() => setIsPasswordHidden(!isPasswordHidden)}
            >
              <Icon
                name={isPasswordHidden ? 'password-hidden' : 'password-shown'}
                group={SVGGroup.Icons}
                width={16}
                height={16}
              />
            </button>
          </div>
          <div
            className={
              createAccountValidation.validPassword
                ? styles.newsletterCheckboxContainerSuccess
                : styles.newsletterCheckboxContainer
            }
          >
            <Icon
              name={createAccountValidation.validPassword ? 'circle-checkmark-green' : 'circle-checkmark-grey'}
              group={SVGGroup.Icons}
              height={16}
              width={16}
            />
            <label
              className={styles.newsletterCheckboxLabel}
              htmlFor='newsletterCheckbox'
            >
              Password must be min 8 characters
            </label>
          </div>
          <div className={styles.newsletterCheckboxContainer}>
            <input
              type='checkbox'
              className={styles.newsletterCheckbox}
              id='newsletterCheckbox'
              data-testid='newsletterCheckbox'
              onChange={handleSubscribedChange}
              checked={shouldSubscribeToNewsletter}
            />
            <label
              className={styles.newsletterCheckboxLabel}
              htmlFor='newsletterCheckbox'
            >
              Sign me up to receive exclusive promotions, news, and other features from PFF via email
            </label>
          </div>
          <Button
            className={styles.createAccountButton}
            text='Create account'
            role='button'
            variant='Primary'
            type='submit'
            disabled={!createAccountValidation.isValid}
          />
        </div>
        <div className={styles.socialMediaOptionContainer}>
          <hr className={styles.optionLine} />
          <span className={styles.socialMediaOptionText}>Or continue with</span>
          <hr className={styles.optionLine} />
        </div>
        <div className={styles.socialMediaButton}>
          {shouldDisplayAppleSignIn && (
            <Button
              variant='SocialMedia'
              onClick={noop}
              data-testid='apple-sign'
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='apple-blue'
              text='Apple'
            />
          )}

          {shouldDisplayGoogleSignIn && (
            <Button
              variant='SocialMedia'
              onClick={noop}
              data-testid='google-sign'
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='google-blue'
              text='Google'
            />
          )}
          {shouldDisplayFacebookSignIn && (
            <Button
              variant='SocialMedia'
              data-testid='facebook-sign'
              onClick={noop}
              size='sm'
              iconWidth='24'
              iconHeight='28'
              iconName='facebook-blue'
              text='Facebook'
            />
          )}
        </div>
        <div className={styles.signInLink}>
          Already have an account?{' '}
          <button
            type='button'
            onClick={() => {
              if (redirectTo) {
                const url = new URL(redirectTo)
                window.location.href = `${redirectBaseUrl}/login?redirect_to=${url.pathname}`
              } else {
                window.location.href = `${redirectBaseUrl}/login`
              }
            }}
          >
            Sign In
          </button>
        </div>
        <ReCAPTCHA
          ref={recaptchaRef}
          size='invisible'
          sitekey={process.env.RECAPTCHA_SITE_KEY!}
        />
      </form>
    </div>
  )
}
